import { Layer, LayerProps } from 'shared/map-exports';
import { usePoisState } from 'state/usePoisState';
import { useCallback } from 'react';
import useMapLayersState from 'state/useMapLayersState';
import addVisible from '../../../shared/addVisible';
import { FILL_PATTERN_LAYER_MIN_ZOOM } from '../constants';
import { useMapLayerEvents } from './useMapLayerEvents';
import { MapLayerProps } from '../types';
import { getSelectedPatternStyle, getSelectedStrokeStyle } from '../utils';
import { BEFORE_LAYER_ID } from '../styles/constants';
import VisibilityToggledSource from './VisibilityToggledSource';
import {
  EXTERNAL_DATA_WATCHDUTY_ORGANIZATION_ID,
  getWatchdutyExternalDataPublicTileUrl,
} from './getWatchdutyTileUrl';

const FIRE_WEATHER_REFRESH_INTERVAL_SECONDS = 3600 * 3; // 3 hours

export const EVENT_TYPE_RED_FLAG_WARNING = 'Red Flag Warning';
const EVENT_TYPE_FIRE_WEATHER_WATCH = 'Fire Weather Watch';
export type AlertType = 'Red Flag Warning' | 'Fire Weather Watch';

const FIRE_WEATHER_SOURCE_LAYER_NAME = 'fire_weather';
const RFW_FILL_LAYER_ID = 'rfw-fill-layer';
const RFW_STROKE_LAYER_ID = 'rfw-stroke-layer';
const RFW_SELECTED_FILL_LAYER_ID = 'rfw-selected-fill-layer';
const RFW_FILL_PATTERN_LAYER_ID = 'rfw-fill-pattern-layer';
const RFW_SELECTED_STROKE_LAYER_ID = 'rfw-selected-stroke-layer';
const MAX_ZOOM_LEVEL = 11;

const commonFilterExpression: Partial<LayerProps> = {
  filter: [
    'case',
    ['==', ['get', 'Event'], EVENT_TYPE_RED_FLAG_WARNING],
    true,
    ['==', ['get', 'Event'], EVENT_TYPE_FIRE_WEATHER_WATCH],
    true,
    false,
  ],
};

const getFillStyle = (isDark: boolean): LayerProps => ({
  ...commonFilterExpression,
  type: 'fill',
  maxzoom: MAX_ZOOM_LEVEL,
  'source-layer': FIRE_WEATHER_SOURCE_LAYER_NAME,
  paint: {
    'fill-color': [
      'case',
      ['==', ['get', 'Event'], EVENT_TYPE_RED_FLAG_WARNING],
      '#FA6FB8',
      '#FF7A00',
    ],
    'fill-opacity': [
      'case',
      ['==', ['get', 'Event'], EVENT_TYPE_RED_FLAG_WARNING],
      0.3,
      isDark ? 0.5 : 0.3,
    ],
  },
  layout: {
    // this will ensure that the oldest events will be on the top of the stack when clicked.
    'fill-sort-key': ['/', 1, ['get', 'Start']],
  },
});

const patternFillStyle: LayerProps = {
  ...commonFilterExpression,
  type: 'fill',
  minzoom: FILL_PATTERN_LAYER_MIN_ZOOM,
  maxzoom: MAX_ZOOM_LEVEL,
  'source-layer': FIRE_WEATHER_SOURCE_LAYER_NAME,
  paint: {
    'fill-pattern': [
      'case',
      ['==', ['get', 'Event'], EVENT_TYPE_RED_FLAG_WARNING],
      'RedFlagWarningPattern',
      'FireWeatherWatchPattern',
    ],
  },
};

const strokeStyle: LayerProps = {
  ...commonFilterExpression,
  type: 'line',
  maxzoom: MAX_ZOOM_LEVEL,
  'source-layer': FIRE_WEATHER_SOURCE_LAYER_NAME,
  paint: {
    'line-color': [
      'case',
      ['==', ['get', 'Event'], EVENT_TYPE_RED_FLAG_WARNING],
      '#FA6FB8',
      '#FF7A00',
    ],
    'line-opacity': 1,
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.25, 18, 3.25],
    'line-dasharray': [2, 1],
  },
};

const RedFlagWarningsLayer = (props: MapLayerProps): JSX.Element => {
  const { visible } = props;
  const { selectedPoi, setSelectedPoi } = usePoisState();
  const { isDarkBaseLayer } = useMapLayersState();

  useMapLayerEvents<object>({
    layerId: RFW_FILL_LAYER_ID,
    onClick: useCallback(
      (geoJsonFeatures) => {
        for (const feature of geoJsonFeatures) {
          setSelectedPoi({ type: 'rfw', id: feature.id });
        }
      },
      [setSelectedPoi],
    ),
  });

  const selectedRFWId = selectedPoi?.type === 'rfw' ? selectedPoi.id || 0 : 0;

  const selectedFillStyle = getSelectedPatternStyle({
    idKey: 'OBJECTID',
    selectedId: selectedRFWId,
    otherLayerProps: {
      'source-layer': FIRE_WEATHER_SOURCE_LAYER_NAME,
    },
  });

  const selectedStrokeStyle = getSelectedStrokeStyle({
    idKey: 'OBJECTID',
    selectedId: selectedRFWId,
    otherLayerProps: {
      'source-layer': FIRE_WEATHER_SOURCE_LAYER_NAME,
    },
  });

  return (
    <VisibilityToggledSource
      visible={visible}
      url={getWatchdutyExternalDataPublicTileUrl(
        EXTERNAL_DATA_WATCHDUTY_ORGANIZATION_ID,
        'fire_weather',
      )}
      refreshIntervalSeconds={FIRE_WEATHER_REFRESH_INTERVAL_SECONDS}
    >
      <Layer
        id={RFW_FILL_LAYER_ID}
        beforeId={BEFORE_LAYER_ID}
        {...addVisible(getFillStyle(isDarkBaseLayer), visible)}
      />
      <Layer
        id={RFW_FILL_PATTERN_LAYER_ID}
        beforeId={BEFORE_LAYER_ID}
        {...addVisible(patternFillStyle, visible)}
      />
      <Layer
        id={RFW_STROKE_LAYER_ID}
        beforeId={BEFORE_LAYER_ID}
        {...addVisible(strokeStyle, visible)}
      />
      <Layer
        id={RFW_SELECTED_FILL_LAYER_ID}
        beforeId={BEFORE_LAYER_ID}
        {...addVisible(selectedFillStyle, visible)}
      />
      <Layer
        id={RFW_SELECTED_STROKE_LAYER_ID}
        beforeId={BEFORE_LAYER_ID}
        {...addVisible(selectedStrokeStyle, visible)}
      />
    </VisibilityToggledSource>
  );
};

export default RedFlagWarningsLayer;
